// ===========================================================================
// Pre-Visit Planning — datos fake deterministas (Módulo D).
//
// No existe backend todavía. Cuando exista, este archivo se elimina y el
// service consumirá el schema `prevvisit` (TAB_HUDDLE_SHEET / TAB_HUDDLE_ITEM)
// vía /api/previsit/*. Ver docs/modulos/BillingV2/MODULO_D_PREVISIT_PLANNING.md
// ===========================================================================

import dayjs from "dayjs";
import { PREP_STATUS, DATE_KEYS } from "../common/constants";

const TODAY = dayjs().format("YYYY-MM-DD");
const TOMORROW = dayjs().add(1, "day").format("YYYY-MM-DD");

/**
 * Citas con su prep packet (huddle sheet) consolidado.
 * Cada cita junta: HCC sospechosos (de Coding Opportunities), care gaps
 * abiertos, meds a reconciliar, últimos labs y agenda sugerida para el PCP.
 */
export const FAKE_APPOINTMENTS = [
  {
    id: "APT-1001",
    dateKey: DATE_KEYS.TODAY,
    date: TODAY,
    time: "08:00",
    visitType: "Annual Wellness",
    status: PREP_STATUS.READY,
    patient: {
      name: "Maria Garcia",
      age: 71,
      gender: "F",
      cipId: "CIP-204881",
      location: "Fort Myers",
      pcp: "Dr. R. Alonso",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "E1165", hcc: "HCC 37", description: "T2DM with hyperglycemia — suggested specificity from E11.9", source: "Coding Opp" },
      { icd: "I509", hcc: "HCC 226", description: "Heart failure, unspecified — confirm and re-document", source: "Prior year" },
      { icd: "N184", hcc: "HCC 327", description: "CKD stage 4 — last eGFR 27, not yet documented this year", source: "Labs" },
    ],
    careGaps: [
      { measure: "HbA1c", description: "HbA1c overdue (last 02/2026, value 9.1%)", due: "Overdue 45d", priority: "high" },
      { measure: "BCS", description: "Mammography not on file in last 27 months", due: "Due now", priority: "medium" },
    ],
    medsToReconcile: [
      { name: "Metformin 1000 mg BID", issue: "Adherence 64% (PDC) — refill gap 18 days" },
      { name: "Furosemide 40 mg QD", issue: "Not on active med list; seen on discharge summary" },
    ],
    lastLabs: [
      { test: "HbA1c", value: "9.1 %", date: "2026-02-10", flag: "crit" },
      { test: "eGFR", value: "27 mL/min", date: "2026-04-22", flag: "crit" },
      { test: "LDL", value: "88 mg/dL", date: "2026-04-22", flag: "ok" },
    ],
    agendaItems: [
      "Confirm CKD stage 4 and document with current eGFR",
      "Assess diabetes control; consider E11.65 specificity",
      "Order in-office HbA1c; schedule mammography",
      "Reconcile furosemide from hospital discharge",
    ],
  },
  {
    id: "APT-1002",
    dateKey: DATE_KEYS.TODAY,
    date: TODAY,
    time: "08:30",
    visitType: "Follow Up",
    status: PREP_STATUS.READY,
    patient: {
      name: "James Holt",
      age: 67,
      gender: "M",
      cipId: "CIP-198223",
      location: "Fort Myers",
      pcp: "Dr. R. Alonso",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "J449", hcc: "HCC 280", description: "COPD documented 2025, not recaptured this year", source: "Prior year" },
    ],
    careGaps: [
      { measure: "COL", description: "Colorectal screening — no FIT/colonoscopy on file", due: "Due now", priority: "medium" },
    ],
    medsToReconcile: [
      { name: "Tiotropium 18 mcg QD", issue: "Refill gap 9 days" },
    ],
    lastLabs: [
      { test: "CBC", value: "WNL", date: "2026-03-14", flag: "ok" },
    ],
    agendaItems: [
      "Recapture COPD with current status and treatment",
      "Order FIT kit; document patient counseling",
    ],
  },
  {
    id: "APT-1003",
    dateKey: DATE_KEYS.TODAY,
    date: TODAY,
    time: "09:00",
    visitType: "Hospital Discharge Follow Up",
    status: PREP_STATUS.PENDING,
    patient: {
      name: "Rosa Linares",
      age: 79,
      gender: "F",
      cipId: "CIP-211504",
      location: "Cape Coral",
      pcp: "Dr. K. Patel",
      membership: "MA PPO",
      sensitive: false,
    },
    suspectedHcc: [
      { icd: "I5023", hcc: "HCC 226", description: "Acute on chronic systolic HF — from discharge summary 06/06", source: "Discharge" },
      { icd: "E785", hcc: "—", description: "Hyperlipidemia — non-HCC, verify statin therapy", source: "Problem list" },
    ],
    careGaps: [
      { measure: "MRP", description: "Medication reconciliation post-discharge within 30 days", due: "6 days left", priority: "high" },
      { measure: "FMC", description: "Follow-up after ED visit — counts if seen today", due: "Today", priority: "high" },
    ],
    medsToReconcile: [
      { name: "Sacubitril/valsartan 49/51 mg BID", issue: "New at discharge — confirm tolerance and BP" },
      { name: "Spironolactone 25 mg QD", issue: "New at discharge — check K+ and renal function" },
      { name: "Aspirin 81 mg QD", issue: "Held at admission; confirm restart" },
    ],
    lastLabs: [
      { test: "BNP", value: "1,240 pg/mL", date: "2026-06-05", flag: "crit" },
      { test: "K+", value: "4.9 mEq/L", date: "2026-06-06", flag: "warn" },
      { test: "Creatinine", value: "1.4 mg/dL", date: "2026-06-06", flag: "warn" },
    ],
    agendaItems: [
      "Complete medication reconciliation (MRP gap closes today)",
      "Document acute on chronic systolic HF with current status",
      "Repeat BMP in 1 week (new spironolactone)",
    ],
  },
  {
    id: "APT-1004",
    dateKey: DATE_KEYS.TODAY,
    date: TODAY,
    time: "09:30",
    visitType: "Annual Wellness",
    status: PREP_STATUS.REVIEWED,
    patient: {
      name: "Albert Nguyen",
      age: 73,
      gender: "M",
      cipId: "CIP-187990",
      location: "Fort Myers",
      pcp: "Dr. R. Alonso",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "F329", hcc: "HCC 155", description: "Major depressive disorder — PHQ-9 = 14 on 05/2026, not documented", source: "Screening" },
    ],
    careGaps: [
      { measure: "EED", description: "Diabetic eye exam not on file this year", due: "Due now", priority: "medium" },
    ],
    medsToReconcile: [],
    lastLabs: [
      { test: "HbA1c", value: "7.2 %", date: "2026-05-02", flag: "warn" },
    ],
    agendaItems: [
      "Review PHQ-9 score; document depression if clinically confirmed",
      "Refer for retinal exam",
    ],
  },
  {
    id: "APT-1005",
    dateKey: DATE_KEYS.TODAY,
    date: TODAY,
    time: "10:00",
    visitType: "Follow Up",
    status: PREP_STATUS.PENDING,
    patient: {
      name: "Carmen Soto",
      age: 64,
      gender: "F",
      cipId: "CIP-225617",
      location: "Cape Coral",
      pcp: "Dr. K. Patel",
      membership: "MA HMO",
      sensitive: true,
    },
    suspectedHcc: [
      { icd: "F1120", hcc: "HCC 135", description: "Opioid use disorder in remission — sensitive, requires explicit documentation", source: "Prior year" },
    ],
    careGaps: [
      { measure: "CBP", description: "BP not controlled at last visit (148/92)", due: "Open", priority: "high" },
    ],
    medsToReconcile: [
      { name: "Lisinopril 20 mg QD", issue: "Consider titration — last BP above goal" },
    ],
    lastLabs: [
      { test: "BMP", value: "WNL", date: "2026-04-30", flag: "ok" },
    ],
    agendaItems: [
      "Recheck BP; titrate antihypertensive if confirmed",
      "Re-document OUD in remission with MEAT support",
    ],
  },
  {
    id: "APT-1006",
    dateKey: DATE_KEYS.TODAY,
    date: TODAY,
    time: "10:30",
    visitType: "Telehealth",
    status: PREP_STATUS.READY,
    patient: {
      name: "Walter Briggs",
      age: 81,
      gender: "M",
      cipId: "CIP-176342",
      location: "Lehigh Acres",
      pcp: "Dr. M. Fuentes",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "I482", hcc: "HCC 238", description: "Chronic atrial fibrillation — on anticoagulant, not documented this year", source: "Med list" },
      { icd: "E119", hcc: "HCC 38", description: "T2DM without complications — verify; neuropathy noted in podiatry note", source: "Specialist note" },
    ],
    careGaps: [],
    medsToReconcile: [
      { name: "Apixaban 5 mg BID", issue: "Confirm renal dosing still appropriate" },
    ],
    lastLabs: [
      { test: "Creatinine", value: "1.1 mg/dL", date: "2026-05-20", flag: "ok" },
    ],
    agendaItems: [
      "Document chronic AFib with anticoagulation plan",
      "Evaluate diabetic neuropathy — consider E11.42",
    ],
  },
  {
    id: "APT-1007",
    dateKey: DATE_KEYS.TODAY,
    date: TODAY,
    time: "11:00",
    visitType: "Follow Up",
    status: PREP_STATUS.PENDING,
    patient: {
      name: "Dolores Ferrer",
      age: 69,
      gender: "F",
      cipId: "CIP-219008",
      location: "Fort Myers",
      pcp: "Dr. R. Alonso",
      membership: "MA PPO",
    },
    suspectedHcc: [],
    careGaps: [
      { measure: "OMW", description: "Osteoporosis management after fracture — no DXA or therapy on file", due: "32 days left", priority: "medium" },
    ],
    medsToReconcile: [],
    lastLabs: [
      { test: "Vitamin D", value: "19 ng/mL", date: "2026-05-11", flag: "warn" },
    ],
    agendaItems: [
      "Order DXA; start vitamin D supplementation",
    ],
  },
  {
    id: "APT-2001",
    dateKey: DATE_KEYS.TOMORROW,
    date: TOMORROW,
    time: "08:00",
    visitType: "Annual Wellness",
    status: PREP_STATUS.READY,
    patient: {
      name: "Hector Maldonado",
      age: 75,
      gender: "M",
      cipId: "CIP-203145",
      location: "Fort Myers",
      pcp: "Dr. R. Alonso",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "I714", hcc: "HCC 270", description: "AAA 4.2 cm on 2025 imaging — never documented in office note", source: "Imaging" },
      { icd: "E1122", hcc: "HCC 37", description: "T2DM with diabetic CKD — eGFR trend supports", source: "Labs" },
    ],
    careGaps: [
      { measure: "EED", description: "Diabetic eye exam not on file this year", due: "Due now", priority: "medium" },
    ],
    medsToReconcile: [
      { name: "Insulin glargine 24 u QHS", issue: "Hypoglycemia reported at last visit — verify dose" },
    ],
    lastLabs: [
      { test: "HbA1c", value: "8.4 %", date: "2026-05-28", flag: "warn" },
      { test: "eGFR", value: "48 mL/min", date: "2026-05-28", flag: "warn" },
    ],
    agendaItems: [
      "Document AAA with surveillance plan",
      "Document diabetic CKD (E11.22 + N18.3)",
      "Address hypoglycemia; adjust insulin",
    ],
  },
  {
    id: "APT-2002",
    dateKey: DATE_KEYS.TOMORROW,
    date: TOMORROW,
    time: "08:30",
    visitType: "Follow Up",
    status: PREP_STATUS.READY,
    patient: {
      name: "Gloria Pineda",
      age: 70,
      gender: "F",
      cipId: "CIP-214772",
      location: "Cape Coral",
      pcp: "Dr. K. Patel",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "M0580", hcc: "HCC 94", description: "Rheumatoid arthritis on methotrexate — recapture", source: "Prior year" },
    ],
    careGaps: [
      { measure: "ART", description: "DMARD therapy documented — verify labs monitoring", due: "Open", priority: "low" },
    ],
    medsToReconcile: [
      { name: "Methotrexate 15 mg weekly", issue: "LFTs due (last 12/2025)" },
    ],
    lastLabs: [
      { test: "AST/ALT", value: "28/31 U/L", date: "2025-12-19", flag: "warn" },
    ],
    agendaItems: [
      "Recapture RA with MEAT; order monitoring labs",
    ],
  },
  {
    id: "APT-2003",
    dateKey: DATE_KEYS.TOMORROW,
    date: TOMORROW,
    time: "09:00",
    visitType: "Annual Wellness",
    status: PREP_STATUS.PENDING,
    patient: {
      name: "Samuel Ortega",
      age: 66,
      gender: "M",
      cipId: "CIP-228430",
      location: "Lehigh Acres",
      pcp: "Dr. M. Fuentes",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "K7030", hcc: "HCC 63", description: "Alcoholic cirrhosis suggested by imaging + thrombocytopenia", source: "Coding Opp" },
    ],
    careGaps: [
      { measure: "COL", description: "Colorectal screening overdue", due: "Overdue 90d", priority: "high" },
      { measure: "AWV", description: "Annual wellness components — HRA pending", due: "Today", priority: "low" },
    ],
    medsToReconcile: [],
    lastLabs: [
      { test: "Platelets", value: "118 K/uL", date: "2026-04-15", flag: "warn" },
      { test: "AST/ALT", value: "62/40 U/L", date: "2026-04-15", flag: "warn" },
    ],
    agendaItems: [
      "Evaluate possible cirrhosis; document if confirmed",
      "Order colonoscopy referral",
    ],
  },
  {
    id: "APT-2004",
    dateKey: DATE_KEYS.TOMORROW,
    date: TOMORROW,
    time: "09:30",
    visitType: "Follow Up",
    status: PREP_STATUS.PENDING,
    patient: {
      name: "Lucia Trevino",
      age: 77,
      gender: "F",
      cipId: "CIP-191236",
      location: "Fort Myers",
      pcp: "Dr. R. Alonso",
      membership: "MA PPO",
    },
    suspectedHcc: [
      { icd: "G20", hcc: "HCC 78", description: "Parkinson's disease — on carbidopa/levodopa, not recaptured", source: "Med list" },
      { icd: "M810", hcc: "—", description: "Osteoporosis — non-HCC, verify therapy", source: "Problem list" },
    ],
    careGaps: [
      { measure: "FRM", description: "Fall risk management — screening positive, no plan documented", due: "Open", priority: "medium" },
    ],
    medsToReconcile: [
      { name: "Carbidopa/levodopa 25/100 TID", issue: "Confirm adherence and motor symptoms" },
    ],
    lastLabs: [],
    agendaItems: [
      "Recapture Parkinson's with current exam findings",
      "Document fall prevention plan",
    ],
  },
  {
    id: "APT-2005",
    dateKey: DATE_KEYS.TOMORROW,
    date: TOMORROW,
    time: "10:00",
    visitType: "Telehealth",
    status: PREP_STATUS.READY,
    patient: {
      name: "Felix Arroyo",
      age: 62,
      gender: "M",
      cipId: "CIP-232901",
      location: "Cape Coral",
      pcp: "Dr. K. Patel",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "C61", hcc: "HCC 21", description: "Prostate cancer on active surveillance — recapture annually", source: "Prior year" },
    ],
    careGaps: [],
    medsToReconcile: [],
    lastLabs: [
      { test: "PSA", value: "6.8 ng/mL", date: "2026-05-30", flag: "warn" },
    ],
    agendaItems: [
      "Document prostate cancer surveillance status with latest PSA",
    ],
  },
  {
    id: "APT-2006",
    dateKey: DATE_KEYS.TOMORROW,
    date: TOMORROW,
    time: "10:30",
    visitType: "Follow Up",
    status: PREP_STATUS.PENDING,
    patient: {
      name: "Beatriz Camacho",
      age: 68,
      gender: "F",
      cipId: "CIP-217554",
      location: "Fort Myers",
      pcp: "Dr. R. Alonso",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "J9601", hcc: "HCC 211", description: "Chronic respiratory failure with hypoxia — on home O2 since 03/2026", source: "DME order" },
      { icd: "J449", hcc: "HCC 280", description: "COPD — recapture with severity", source: "Prior year" },
    ],
    careGaps: [
      { measure: "SPR", description: "Spirometry to confirm COPD — never performed", due: "Open", priority: "medium" },
    ],
    medsToReconcile: [
      { name: "Budesonide/formoterol BID", issue: "Verify technique and adherence" },
    ],
    lastLabs: [
      { test: "SpO2 (resting)", value: "89 %", date: "2026-06-01", flag: "crit" },
    ],
    agendaItems: [
      "Document chronic respiratory failure with home O2",
      "Order spirometry",
    ],
  },
  {
    id: "APT-2007",
    dateKey: DATE_KEYS.TOMORROW,
    date: TOMORROW,
    time: "11:00",
    visitType: "Annual Wellness",
    status: PREP_STATUS.READY,
    patient: {
      name: "Oscar Villalba",
      age: 74,
      gender: "M",
      cipId: "CIP-208119",
      location: "Lehigh Acres",
      pcp: "Dr. M. Fuentes",
      membership: "MA HMO",
    },
    suspectedHcc: [
      { icd: "I252", hcc: "HCC 222", description: "Old MI (2023) — never recaptured since", source: "Prior year" },
      { icd: "E780", hcc: "—", description: "Pure hypercholesterolemia — verify statin intensity", source: "Problem list" },
    ],
    careGaps: [
      { measure: "SPC", description: "Statin therapy for CVD — moderate intensity, consider high", due: "Open", priority: "low" },
    ],
    medsToReconcile: [
      { name: "Atorvastatin 20 mg QD", issue: "Consider 40-80 mg given ASCVD history" },
    ],
    lastLabs: [
      { test: "LDL", value: "112 mg/dL", date: "2026-05-05", flag: "warn" },
    ],
    agendaItems: [
      "Document history of MI (I25.2)",
      "Intensify statin if appropriate",
    ],
  },
];
